import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NAV_BADGES_KEY } from "@/hooks/useNavBadges";
import { whatsNewApi } from "../api/whatsNew";
import type { AnnouncementFormData, AnnouncementStatus } from "../types";

export const KEYS = {
  all: ["announcements"] as const,
  list: (status?: AnnouncementStatus) => ["announcements", "list", status ?? "all"] as const,
  audience: (id: string) => ["announcements", "audience", id] as const,
  loginChanges: ["announcements", "login-changes"] as const,
};

/** Recipient list + read receipts — fetched only once the panel is expanded. */
export function useAnnouncementAudience(id: string, enabled: boolean) {
  return useQuery({
    queryKey: KEYS.audience(id),
    queryFn: () => whatsNewApi.audience(id),
    enabled,
  });
}

export function useAnnouncements(status?: AnnouncementStatus) {
  return useQuery({
    queryKey: KEYS.list(status),
    queryFn: () => whatsNewApi.list(status),
  });
}

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AnnouncementFormData) => whatsNewApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useUpdateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AnnouncementFormData> }) =>
      whatsNewApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => whatsNewApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function usePublishAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isImportant = false }: { id: string; isImportant?: boolean }) =>
      whatsNewApi.publish(id, isImportant),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: NAV_BADGES_KEY });
    },
  });
}

export function useMarkAnnouncementRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => whatsNewApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: NAV_BADGES_KEY });
    },
  });
}
